import * as THREE from "three";

const DEFAULT_VERTICAL_SEGMENTS = 10;
const PROFILE_EASING_POWER = 1.6;

/**
 * Builds one continuous inner basin wall between two matching plan outlines.
 * The eased profile stays upright beneath the rim, then turns progressively
 * toward the smaller floor outline instead of forming stacked ledges.
 */
export function createSlopedBasinBufferGeometry(
  topPoints: [number, number][],
  bottomPoints: [number, number][],
  topZ: number,
  bottomZ: number,
  verticalSegments = DEFAULT_VERTICAL_SEGMENTS,
): THREE.BufferGeometry {
  if (topPoints.length < 3 || topPoints.length !== bottomPoints.length) {
    throw new Error("Sloped basin outlines must have the same point count");
  }

  const ringSize = topPoints.length;
  const segmentCount = Math.max(1, Math.round(verticalSegments));
  const positions: number[] = [];
  const indices: number[] = [];

  for (let level = 0; level <= segmentCount; level += 1) {
    const t = level / segmentCount;
    const profileT = Math.pow(t, PROFILE_EASING_POWER);
    const z = THREE.MathUtils.lerp(topZ, bottomZ, t);

    for (let pointIndex = 0; pointIndex < ringSize; pointIndex += 1) {
      const [topX, topY] = topPoints[pointIndex];
      const [bottomX, bottomY] = bottomPoints[pointIndex];
      positions.push(
        THREE.MathUtils.lerp(topX, bottomX, profileT),
        THREE.MathUtils.lerp(topY, bottomY, profileT),
        z,
      );
    }
  }

  for (let level = 0; level < segmentCount; level += 1) {
    const currentRing = level * ringSize;
    const nextRing = (level + 1) * ringSize;
    for (let pointIndex = 0; pointIndex < ringSize; pointIndex += 1) {
      const nextPoint = (pointIndex + 1) % ringSize;
      const topCurrent = currentRing + pointIndex;
      const topNext = currentRing + nextPoint;
      const bottomCurrent = nextRing + pointIndex;
      const bottomNext = nextRing + nextPoint;
      indices.push(
        topCurrent,
        bottomCurrent,
        topNext,
        topNext,
        bottomCurrent,
        bottomNext,
      );
    }
  }

  const geometry = new THREE.BufferGeometry();
  geometry.setAttribute(
    "position",
    new THREE.Float32BufferAttribute(positions, 3),
  );
  geometry.setIndex(indices);
  geometry.computeVertexNormals();
  geometry.computeBoundingBox();
  geometry.computeBoundingSphere();
  return geometry;
}
